/**
 * Patient Journey Dashboard Component
 * 
 * Comprehensive view of patient's hospice journey with:
 * - Phase visualization with detailed requirements
 * - Task management
 * - Compliance alerts
 * - Timeline view
 * - Orchestration status
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle,
  Circle,
  Clock,
  AlertTriangle,
  AlertCircle,
  User,
  FileText,
  Calendar,
  Activity,
  ClipboardList,
  Loader2,
  ChevronRight,
  Play,
  Flag,
  Users,
  Stethoscope,
  Heart,
  Target,
  CheckSquare,
  XCircle,
  Timer,
  Shield,
  TrendingUp,
  Bell,
  BellRing,
  X
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Phase configuration
const PHASES = [
  { id: "intake", label: "Intake", icon: FileText, color: "bg-blue-500", description: "Referral reception and initial screening" },
  { id: "eligibility", label: "Eligibility", icon: ClipboardList, color: "bg-indigo-500", description: "Terminal prognosis and certification" },
  { id: "pre_admission", label: "Pre-Admission", icon: Users, color: "bg-purple-500", description: "Team assignment and scheduling" },
  { id: "admission", label: "Admission", icon: Stethoscope, color: "bg-teal-500", description: "HOPE assessment and initial POC" },
  { id: "active_care", label: "Active Care", icon: Heart, color: "bg-green-500", description: "Ongoing visits and IDG meetings" },
  { id: "recertification", label: "Recertification", icon: Calendar, color: "bg-amber-500", description: "F2F and benefit period renewal" },
  { id: "discharge", label: "Discharge", icon: Flag, color: "bg-slate-500", description: "End of hospice services" },
];

const PRIORITY_COLORS = {
  critical: "bg-red-500",
  high: "bg-amber-500",
  medium: "bg-blue-500",
  low: "bg-slate-500"
};

const STATUS_COLORS = {
  pending: "bg-slate-400",
  in_progress: "bg-blue-500",
  completed: "bg-green-500",
  overdue: "bg-red-500",
  escalated: "bg-amber-500",
  not_started: "bg-slate-600"
};

const COMPLIANCE_COLORS = {
  ok: "text-green-400",
  warning: "text-amber-400",
  critical: "text-red-400"
};

// Requirement status icon component
const RequirementStatusIcon = ({ status }) => {
  switch (status) {
    case "completed":
      return <CheckCircle className="w-4 h-4 text-green-400" />;
    case "in_progress":
      return <Timer className="w-4 h-4 text-blue-400 animate-pulse" />;
    case "overdue":
      return <XCircle className="w-4 h-4 text-red-400" />;
    default:
      return <Circle className="w-4 h-4 text-slate-500" />;
  }
};

export default function PatientJourneyDashboard({ patientId, patientName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [journey, setJourney] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [processingTask, setProcessingTask] = useState(null);
  const [allPhases, setAllPhases] = useState([]);
  const [currentPhaseDetail, setCurrentPhaseDetail] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationStats, setNotificationStats] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      
      const [notifResponse, statsResponse] = await Promise.all([
        fetch(`${API}/api/patient-journey/notifications/active?limit=20`, {
          headers: { "Authorization": `Bearer ${token}` }
        }),
        fetch(`${API}/api/patient-journey/notifications/stats`, {
          headers: { "Authorization": `Bearer ${token}` }
        })
      ]);
      
      if (notifResponse.ok) {
        const data = await notifResponse.json();
        setNotifications(data.notifications || []);
      }
      
      if (statsResponse.ok) {
        const stats = await statsResponse.json();
        setNotificationStats(stats);
      }
    } catch (error) {
      console.error("Notification load error:", error);
    }
  }, []);

  const dismissNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API}/api/patient-journey/notifications/${notificationId}/dismiss`,
        {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        loadNotifications();
      }
    } catch (error) {
      toast.error("Failed to dismiss notification");
    }
  };

  const loadJourney = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("hospice_token");
      
      // Try the new enhanced endpoint first
      let response = await fetch(
        `${API}/api/patient-journey/orchestration/${patientId}/full-status`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        // Fallback to legacy endpoint
        response = await fetch(
          `${API}/api/patient-journey/patient/${patientId}`,
          { headers: { "Authorization": `Bearer ${token}` } }
        );
      }
      
      if (!response.ok) throw new Error("Failed to load journey");
      
      const data = await response.json();
      
      if (data.found) {
        setJourney(data.journey);
        setTasks(data.pending_tasks || data.tasks || []);
        setAlerts(data.active_alerts || data.alerts || []);
        setSummary(data.summary);
        setAllPhases(data.all_phases || []);
        setCurrentPhaseDetail(data.current_phase_detail || null);
        
        // Auto-expand current phase
        if (data.journey?.current_phase) {
          setExpandedPhase(data.journey.current_phase);
        }
      } else {
        // No journey exists - show option to start one
        setJourney(null);
      }
    } catch (error) {
      console.error("Journey load error:", error);
      toast.error("Failed to load patient journey");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadJourney();
    loadNotifications();
  }, [loadJourney, loadNotifications]);

  const startJourney = async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API}/api/patient-journey/start?patient_id=${patientId}&patient_name=${encodeURIComponent(patientName)}`,
        {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success("Patient journey started successfully");
        loadJourney();
      } else {
        toast.error(data.error || "Failed to start journey");
      }
    } catch (error) {
      toast.error("Failed to start journey");
    }
  };

  const completeTask = async (taskId) => {
    setProcessingTask(taskId);
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API}/api/patient-journey/task/complete/${taskId}`,
        {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success("Task completed");
        loadJourney();
      } else {
        toast.error("Failed to complete task");
      }
    } catch (error) {
      toast.error("Failed to complete task");
    } finally {
      setProcessingTask(null);
    }
  };

  const advancePhase = async (newPhase, force = false) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API}/api/patient-journey/orchestration/${patientId}/advance-with-validation?target_phase=${newPhase}&force=${force}`,
        {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Advanced to ${newPhase.replace("_", " ")} phase`);
        loadJourney();
      } else if (data.can_advance === false) {
        // Show incomplete requirements
        const reqList = data.incomplete_requirements?.map(r => r.title).join(", ");
        toast.warning(`Complete these first: ${reqList}`, { duration: 5000 });
      } else {
        toast.error(data.message || "Failed to advance phase");
      }
    } catch (error) {
      toast.error("Failed to advance phase");
    }
  };

  const completeRequirement = async (requirementId) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API}/api/patient-journey/orchestration/${patientId}/complete-requirement?requirement_id=${requirementId}`,
        {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success("Requirement completed");
        loadJourney();
      } else {
        toast.error("Failed to complete requirement");
      }
    } catch (error) {
      toast.error("Failed to complete requirement");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  // No journey exists
  if (!journey) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-8 text-center">
          <Activity className="w-16 h-16 mx-auto mb-4 text-slate-500" />
          <h3 className="text-xl font-semibold text-white mb-2">No Active Journey</h3>
          <p className="text-slate-400 mb-6">
            This patient does not have an active hospice journey. Start one to begin tracking their care.
          </p>
          <Button 
            className="bg-teal-600 hover:bg-teal-700"
            onClick={startJourney}
          >
            <Play className="w-4 h-4 mr-2" />
            Start Patient Journey
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Get current phase index
  const currentPhaseIndex = PHASES.findIndex(p => p.id === journey.current_phase);

  return (
    <div className="space-y-4" data-testid="patient-journey-dashboard">
      {/* Notification Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-white">Patient Journey</h2>
          <span className="text-slate-400">•</span>
          <span className="text-slate-400">{patientName}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="relative"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            {notificationStats?.critical > 0 ? (
              <BellRing className="w-5 h-5 text-amber-400" />
            ) : (
              <Bell className="w-5 h-5 text-slate-400" />
            )}
            {notificationStats?.active_notifications > 0 && (
              <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs">
                {notificationStats.active_notifications > 9 ? '9+' : notificationStats.active_notifications}
              </Badge>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Notification Panel */}
      {showNotifications && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-400" />
                Notifications
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNotifications(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {notifications.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No notifications</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-3 rounded border ${
                        notification.severity === 'critical' ? 'bg-red-900/20 border-red-600/30' :
                        notification.severity === 'warning' ? 'bg-amber-900/20 border-amber-600/30' :
                        'bg-slate-900/30 border-slate-700'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={`text-xs ${
                              notification.severity === 'critical' ? 'bg-red-500/30 text-red-300' :
                              notification.severity === 'warning' ? 'bg-amber-500/30 text-amber-300' :
                              'bg-slate-500/30 text-slate-300'
                            }`}>
                              {notification.severity}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {notification.notification_type?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm text-white font-medium">{notification.title}</p>
                          <p className="text-xs text-slate-400 mt-1">{notification.message}</p>
                          <p className="text-xs text-slate-500 mt-2">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => dismissNotification(notification.id)}
                          className="text-slate-400 hover:text-white"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Phase Progress */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-teal-400" />
            Journey Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Phase Timeline */}
          <div className="flex items-center justify-between mb-4">
            {PHASES.slice(0, -1).map((phase, index) => {
              const PhaseIcon = phase.icon;
              const isCompleted = index < currentPhaseIndex;
              const isCurrent = index === currentPhaseIndex;
              const isUpcoming = index > currentPhaseIndex;
              
              return (
                <React.Fragment key={phase.id}>
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted ? "bg-green-500" :
                      isCurrent ? phase.color :
                      "bg-slate-700"
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : (
                        <PhaseIcon className={`w-5 h-5 ${isCurrent ? "text-white" : "text-slate-500"}`} />
                      )}
                    </div>
                    <span className={`text-xs mt-1 ${
                      isCurrent ? "text-white font-medium" : "text-slate-500"
                    }`}>
                      {phase.label}
                    </span>
                  </div>
                  {index < PHASES.length - 2 && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      index < currentPhaseIndex ? "bg-green-500" : "bg-slate-700"
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Phase Progress</span>
              <span>{Math.round((currentPhaseIndex / (PHASES.length - 2)) * 100)}%</span>
            </div>
            <Progress value={(currentPhaseIndex / (PHASES.length - 2)) * 100} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <Card className={`border ${
          alerts.some(a => a.severity === "critical") 
            ? "bg-red-900/30 border-red-600/50" 
            : "bg-amber-900/30 border-amber-600/50"
        }`}>
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                alerts.some(a => a.severity === "critical") ? "text-red-400" : "text-amber-400"
              }`} />
              <div className="flex-1">
                <p className="font-medium text-white text-sm">
                  {alerts.length} Active Alert{alerts.length > 1 ? "s" : ""}
                </p>
                <div className="space-y-1 mt-2">
                  {alerts.slice(0, 3).map((alert, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className={alert.severity === "critical" ? "text-red-300" : "text-amber-300"}>
                        {alert.title}
                      </span>
                      <Badge className={`text-xs ${
                        alert.severity === "critical" ? "bg-red-500/30" : "bg-amber-500/30"
                      }`}>
                        {alert.severity}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-4 gap-3">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-white">{summary.total_tasks}</p>
              <p className="text-xs text-slate-400">Total Tasks</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-400">{summary.completed}</p>
              <p className="text-xs text-slate-400">Completed</p>
            </CardContent>
          </Card>
          <Card className={`border-slate-700 ${summary.overdue > 0 ? "bg-red-900/20" : "bg-slate-800/50"}`}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${summary.overdue > 0 ? "text-red-400" : "text-slate-400"}`}>
                {summary.overdue}
              </p>
              <p className="text-xs text-slate-400">Overdue</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${
                summary.compliance_score >= 90 ? "text-green-400" :
                summary.compliance_score >= 70 ? "text-amber-400" : "text-red-400"
              }`}>
                {summary.compliance_score}%
              </p>
              <p className="text-xs text-slate-400">Compliance</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 bg-slate-800">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="phases">Phases</TabsTrigger>
          <TabsTrigger value="tasks">Tasks ({tasks.length})</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {/* Current Phase Progress */}
          {currentPhaseDetail && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Target className="w-4 h-4 text-teal-400" />
                    Current Phase: {currentPhaseDetail.phase_name}
                  </CardTitle>
                  <Badge className={`${
                    currentPhaseDetail.compliance_status === "ok" ? "bg-green-500/20 text-green-400" :
                    currentPhaseDetail.compliance_status === "warning" ? "bg-amber-500/20 text-amber-400" :
                    "bg-red-500/20 text-red-400"
                  }`}>
                    {currentPhaseDetail.compliance_status === "ok" ? "On Track" :
                     currentPhaseDetail.compliance_status === "warning" ? "Needs Attention" : "Critical"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span>Phase Completion</span>
                    <span>{Math.round(currentPhaseDetail.progress_percent)}%</span>
                  </div>
                  <Progress value={currentPhaseDetail.progress_percent} className="h-2" />
                </div>
                
                {/* Phase Requirements */}
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Requirements</p>
                  {currentPhaseDetail.requirements?.slice(0, 5).map((req) => (
                    <div 
                      key={req.id}
                      className={`flex items-center justify-between p-2 rounded ${
                        req.status === "overdue" ? "bg-red-900/20" :
                        req.status === "completed" ? "bg-green-900/10" :
                        "bg-slate-900/30"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <RequirementStatusIcon status={req.status} />
                        <div>
                          <p className="text-sm text-white">{req.title}</p>
                          {req.deadline && req.status !== "completed" && (
                            <p className="text-xs text-slate-500">
                              Due: {new Date(req.deadline).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      {req.status !== "completed" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-teal-400 hover:text-teal-300"
                          onClick={() => completeRequirement(req.id)}
                        >
                          <CheckSquare className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Next Actions */}
                {currentPhaseDetail.next_actions?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Next Actions</p>
                    <div className="flex flex-wrap gap-2">
                      {currentPhaseDetail.next_actions.slice(0, 3).map((action, i) => (
                        <Badge key={i} variant="outline" className={`text-xs ${
                          action.priority === "critical" ? "border-red-500 text-red-400" :
                          action.priority === "high" ? "border-amber-500 text-amber-400" :
                          "border-slate-500"
                        }`}>
                          {action.title}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Journey Details */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Journey Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Current Phase</p>
                <p className="text-white font-medium capitalize">{journey.current_phase.replace("_", " ")}</p>
              </div>
              <div>
                <p className="text-slate-500">Days in Phase</p>
                <p className="text-white">{summary?.days_in_current_phase || 0} days</p>
              </div>
              <div>
                <p className="text-slate-500">Referral Source</p>
                <p className="text-white capitalize">{journey.referral_source || "Not specified"}</p>
              </div>
              <div>
                <p className="text-slate-500">Benefit Period</p>
                <p className="text-white">{journey.current_benefit_period}</p>
              </div>
              <div>
                <p className="text-slate-500">RNCM</p>
                <p className="text-white">{journey.rncm_name || "Not assigned"}</p>
              </div>
              <div>
                <p className="text-slate-500">Attending Physician</p>
                <p className="text-white">{journey.attending_physician_name || "Not assigned"}</p>
              </div>
            </CardContent>
          </Card>

          {/* Key Dates */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-400" />
                Key Dates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {journey.admission_date && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Admission Date</span>
                  <span className="text-white">{new Date(journey.admission_date).toLocaleDateString()}</span>
                </div>
              )}
              {journey.next_idg_date && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Next IDG</span>
                  <Badge variant={
                    new Date(journey.next_idg_date) < new Date() ? "destructive" : "outline"
                  }>
                    {new Date(journey.next_idg_date).toLocaleDateString()}
                  </Badge>
                </div>
              )}
              {journey.next_recert_date && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Next Recertification</span>
                  <Badge variant={
                    new Date(journey.next_recert_date) < new Date() ? "destructive" : "outline"
                  }>
                    {new Date(journey.next_recert_date).toLocaleDateString()}
                  </Badge>
                </div>
              )}
              {journey.f2f_scheduled && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">F2F Scheduled</span>
                  <span className="text-white">{new Date(journey.f2f_scheduled).toLocaleDateString()}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Phase Actions */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Phase Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {journey.current_phase === "intake" && (
                <Button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => advancePhase("eligibility")}
                >
                  Complete Intake → Move to Eligibility
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {journey.current_phase === "eligibility" && (
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={() => advancePhase("pre_admission")}
                >
                  Confirm Eligibility → Pre-Admission
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {journey.current_phase === "pre_admission" && (
                <Button 
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  onClick={() => advancePhase("admission")}
                >
                  Begin Admission
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {journey.current_phase === "admission" && (
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => advancePhase("active_care")}
                >
                  Complete Admission → Active Care
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {journey.current_phase === "active_care" && (
                <Button 
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  onClick={() => advancePhase("recertification")}
                >
                  Start Recertification Process
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {journey.current_phase === "recertification" && (
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => advancePhase("active_care")}
                >
                  Complete Recert → Return to Active Care
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Phases Tab - New detailed view */}
        <TabsContent value="phases" className="mt-4">
          <ScrollArea className="h-[500px]">
            <Accordion 
              type="single" 
              collapsible 
              value={expandedPhase}
              onValueChange={setExpandedPhase}
              className="space-y-2"
            >
              {allPhases.map((phase) => {
                const phaseConfig = PHASES.find(p => p.id === phase.phase_id);
                const PhaseIcon = phaseConfig?.icon || Activity;
                
                return (
                  <AccordionItem 
                    key={phase.phase_id} 
                    value={phase.phase_id}
                    className={`border rounded-lg ${
                      phase.status === "completed" ? "border-green-600/30 bg-green-900/10" :
                      phase.status === "in_progress" ? "border-teal-600/50 bg-teal-900/20" :
                      "border-slate-700 bg-slate-800/50"
                    }`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:no-underline">
                      <div className="flex items-center gap-3 w-full">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          phase.status === "completed" ? "bg-green-500" :
                          phase.status === "in_progress" ? phaseConfig?.color || "bg-teal-500" :
                          "bg-slate-700"
                        }`}>
                          {phase.status === "completed" ? (
                            <CheckCircle className="w-4 h-4 text-white" />
                          ) : (
                            <PhaseIcon className={`w-4 h-4 ${
                              phase.status === "in_progress" ? "text-white" : "text-slate-500"
                            }`} />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <p className={`font-medium ${
                            phase.status === "in_progress" ? "text-white" : "text-slate-400"
                          }`}>
                            {phase.phase_name}
                          </p>
                          <p className="text-xs text-slate-500">{phaseConfig?.description}</p>
                        </div>
                        <div className="flex items-center gap-2 mr-4">
                          <Badge className={`text-xs ${
                            phase.compliance_status === "ok" ? "bg-green-500/20 text-green-400" :
                            phase.compliance_status === "warning" ? "bg-amber-500/20 text-amber-400" :
                            "bg-red-500/20 text-red-400"
                          }`}>
                            {Math.round(phase.progress_percent)}%
                          </Badge>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      {/* Phase Requirements */}
                      <div className="space-y-2 mt-2">
                        {phase.requirements?.map((req) => (
                          <div 
                            key={req.id}
                            className={`flex items-center justify-between p-2 rounded ${
                              req.status === "overdue" ? "bg-red-900/30 border border-red-600/30" :
                              req.status === "completed" ? "bg-green-900/20" :
                              req.status === "in_progress" ? "bg-blue-900/20" :
                              "bg-slate-900/50"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <RequirementStatusIcon status={req.status} />
                              <div>
                                <p className="text-sm text-white">{req.title}</p>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    {req.source_module}
                                  </Badge>
                                  {req.deadline && req.status !== "completed" && (
                                    <span className="text-xs text-slate-500">
                                      Due: {new Date(req.deadline).toLocaleDateString()}
                                    </span>
                                  )}
                                  {req.completed_at && (
                                    <span className="text-xs text-green-500">
                                      ✓ {new Date(req.completed_at).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {req.status !== "completed" && phase.status === "in_progress" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-teal-400 hover:text-teal-300"
                                onClick={() => completeRequirement(req.id)}
                              >
                                <CheckSquare className="w-4 h-4 mr-1" />
                                Complete
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Phase dates */}
                      {(phase.started_at || phase.completed_at) && (
                        <div className="mt-4 pt-3 border-t border-slate-700 text-xs text-slate-500">
                          {phase.started_at && (
                            <span>Started: {new Date(phase.started_at).toLocaleString()}</span>
                          )}
                          {phase.completed_at && (
                            <span className="ml-4">Completed: {new Date(phase.completed_at).toLocaleString()}</span>
                          )}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </ScrollArea>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-3 mt-4">
          {tasks.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-8 text-center text-slate-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No pending tasks</p>
              </CardContent>
            </Card>
          ) : (
            tasks.map((task) => (
              <Card 
                key={task.id}
                className={`border ${
                  task.status === "overdue" 
                    ? "bg-red-900/20 border-red-600/50" 
                    : "bg-slate-800/50 border-slate-700"
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-xs ${PRIORITY_COLORS[task.priority]}`}>
                          {task.priority}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {task.assigned_role}
                        </Badge>
                        {task.cms_deadline && (
                          <Badge variant="outline" className="text-xs border-amber-500 text-amber-300">
                            CMS Deadline
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-medium text-white">{task.title}</h4>
                      {task.description && (
                        <p className="text-sm text-slate-400 mt-1">{task.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </span>
                        {task.assigned_to_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {task.assigned_to_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Badge className={STATUS_COLORS[task.status]}>
                        {task.status.replace("_", " ")}
                      </Badge>
                      {task.status !== "completed" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => completeTask(task.id)}
                          disabled={processingTask === task.id}
                        >
                          {processingTask === task.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Phase History</CardTitle>
            </CardHeader>
            <CardContent>
              {journey.phase_history && journey.phase_history.length > 0 ? (
                <div className="space-y-4">
                  {journey.phase_history.map((entry, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                        <ArrowRight className="w-4 h-4 text-teal-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white">
                          <span className="capitalize">{entry.from_phase?.replace("_", " ")}</span>
                          {" → "}
                          <span className="capitalize font-medium">{entry.to_phase?.replace("_", " ")}</span>
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(entry.timestamp).toLocaleString()}
                        </p>
                        {entry.notes && (
                          <p className="text-sm text-slate-400 mt-1">{entry.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>Journey just started - no phase changes yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
